import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import { useEffect, useMemo, useState } from "react";
import modelsHelper from "../../API/Models/modelsHelper";
import finetuningHelper from "../../API/FineTuning/finetuningHelper";
import ModelPricingInfo from "../ModelPricingInfo";
import { findTrainingLogIdForModel } from "./FineTunedModelStatusChip";

// Fine-tuned tier -> ModelPricingInfo size badge label.
const TIER_TO_MODEL_TYPE = {
  small: "small",
  medium: "medium",
  large: "large",
  xl: "extraLarge",
  xxl: "ultra",
};

// Exact per-token pricing for a fine-tuned base model. Fine-tuned models are
// absent from the models-list templates (so ModelPricingInfo would fall back
// to a generic medium tier) — their real price is the flat per-1M-token rate
// for the base model's size tier, served by GET /finetuning/pricing.
// Non-fine-tuned models render the normal ModelPricingInfo.
const FineTunedModelPricingInfo = ({ modelId, token, ...pricingInfoProps }) => {
  const { state } = AppStateManager.useAppStateManager();

  const [pricing, setPricing] = useState(null);

  const trainingLogId = useMemo(
    () => findTrainingLogIdForModel(state?.trainingLogs, modelId),
    [JSON.stringify(state?.trainingLogs), modelId]
  );

  useEffect(() => {
    if (!trainingLogId || !token) {
      setPricing(null);
      return;
    }
    let cancelled = false;
    finetuningHelper
      .getPricing({ token, trainingLogId })
      .then((res) => {
        if (!cancelled && res?.pricing) setPricing(res);
        else if (!cancelled) setPricing(null);
      })
      .catch(() => {
        if (!cancelled) setPricing(null);
      });
    return () => {
      cancelled = true;
    };
  }, [trainingLogId, token]);

  // Not a fine-tuned model of this workspace -> normal template pricing.
  if (!trainingLogId) {
    return <ModelPricingInfo modelId={modelId} {...pricingInfoProps} />;
  }

  // Fine-tuned but pricing not (yet) available -> show nothing rather than a
  // wrong generic tier price.
  if (!pricing) return null;

  const p = pricing.pricing || {};
  return (
    <ModelPricingInfo
      modelId={modelId}
      {...pricingInfoProps}
      // The separator is a fine-tuned-model-only affordance.
      showSeparator={false}
      pricingOverride={{
        modelType: TIER_TO_MODEL_TYPE[pricing.tier] || "medium",
        inputUoiPer1mTokens: modelsHelper.usdToUoi(p.input_per_1m_tokens),
        outputUoiPer1mTokens: modelsHelper.usdToUoi(p.output_per_1m_tokens),
        // Rendered as the warm-lane chip inside the pricing badges row.
        warmHourlyUoi: modelsHelper.usdToUoi(p.warm_hourly_usd),
      }}
    />
  );
};

export default FineTunedModelPricingInfo;
